import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { branchApi } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import { formatDateTime } from "../utils/formatDateTime";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import PaginationControls from "../components/ui/PaginationControls";
import Breadcrumb from "../components/Breadcrumb";
import { TextWrap } from "../utils/textWrap";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

const LocationsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [branchFilter, setBranchFilter] = useState(
    location.state?.branchFilter || null
  );
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        location_id: "location_id",
        location_name: "location_name",
        branch_name: "branch_name",
        branch_id: "branch_id",
        location_code: "location_code",
        notes: "notes",
        is_active: "is_active",
        created_by: "created_by",
        created_datetime: "created_datetime",
      };

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
        ...(branchFilter && { branch_id: branchFilter.id }),
      };

      const response = await branchApi.getAllBranchLocations(params);

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: data is not an array");
      }
      if (!response.pagination || typeof response.pagination !== "object") {
        throw new Error("Invalid API response: pagination data missing");
      }

      let data = response.data.map((location, index) => ({
        id: location.location_id || index + 1,
        location_id: location.location_id || "-",
        location_name: location.location_name || "-",
        branch_name: location.branch_name || "-",
        branch_id: location.branch_id || "-",
        location_code: location.location_code || "-",
        notes: location.notes || "-",
        is_active:
          location.is_active !== undefined && location.is_active !== null
            ? location.is_active
              ? "Active"
              : "Inactive"
            : "-",
        created_by: location.created_by || "-",
        created_datetime: location.created_datetime || "-",
        display_created_datetime: location.created_datetime
          ? formatDateTime(location.created_datetime)
          : "-",
        polygons: location.polygons || [],
      }));

      if (sortConfig.field) {
        data = [...data].sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setLocations(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
      setError(null);
      handleApiResponse(response, "fetch locations", "GET");
    } catch (err) {
      const errorMessage =
        err?.message ||
        "Failed to fetch locations. Please check the API endpoint.";
      setError(errorMessage);
      setLocations([]);
      handleApiResponse(err, "Failed to fetch locations", "GET");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    navigate("/branch-location-edit", { state: { locationData: location } });
  };

  const handleDeleteClick = (locationId, locationName) => {
    setLocationToDelete({ id: locationId, name: locationName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (locationToDelete) {
      setLoading(true);
      try {
        const res = await branchApi.deleteBranchLocation(locationToDelete.id);
        handleApiResponse(
          res,
          `Location "${locationToDelete.name}" deleted successfully`,
          "DELETE"
        );

        setLocations((prev) =>
          prev.filter((loc) => loc.id !== locationToDelete.id)
        );
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          currentPage: Math.min(
            prev.currentPage,
            Math.ceil((prev.total - 1) / prev.perPage) || 1
          ),
        }));
        setShowDeleteModal(false);
        setLocationToDelete(null);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || "Failed to delete location";
        handleApiResponse(err, "Failed to delete location", "DELETE");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setLocationToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({
      ...prev,
      perPage: value,
      currentPage: 1,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  useEffect(() => {
    fetchLocations();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
    branchFilter,
  ]);

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchLocations} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search By Location Name or Branch Name"
                placeholder="Search"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search locations"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2"> {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Location List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Location"
              permissionKey="add_branch_location"
              onClick={() =>
                navigate("/branch-location-create", { state: { branchFilter } })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Location List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {locations.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No locations match your search or filter."
                  : "No locations available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("location_code")}
                    role="button"
                    aria-label={`Sort by location code, currently sorted ${
                      sortConfig.field === "location_code"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Location Code
                      <SortIcons
                        field="location_code"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("location_name")}
                    role="button"
                    aria-label={`Sort by location name, currently sorted ${
                      sortConfig.field === "location_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Location Name
                      <SortIcons
                        field="location_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_name")}
                    role="button"
                    aria-label={`Sort by branch name, currently sorted ${
                      sortConfig.field === "branch_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Branch Name
                      <SortIcons field="branch_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("notes")}
                    role="button"
                    aria-label={`Sort by notes, currently sorted ${
                      sortConfig.field === "notes"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Notes
                      <SortIcons field="notes" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location, index) => (
                  <tr key={location.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {location.location_code}
                      </span>
                    </td>
                    <TextWrap location={location.location_name} />
                    <td>
                      <span
                        className="text-md mb-0 fw-normal text-secondary-light cursor-pointer text-primary-600 hover:text-primary-800"
                        onClick={() => navigate("/branch-list")}
                      >
                        {location.branch_name}
                      </span>
                    </td>
                    <TextWrap location={location.notes} />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {location.created_by}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={location.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {formatDateTime(location.created_datetime)}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("update_branch_location") && (
                          <button
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Edit ${location.location_name}`}
                            aria-label={`Edit location ${location.location_name}`}
                            onClick={() => handleEdit(location)}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("delete_branch_location") && (
                          <button
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Delete ${location.location_name}`}
                            aria-label={`Delete location ${location.location_name}`}
                            onClick={() =>
                              handleDeleteClick(
                                location.id,
                                location.location_name
                              )
                            }
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>{locationToDelete?.name || "this location"}</strong>?
              </p>
              <p className="text-neutral-900 small">
                If you delete this location, all associated slots will also be
                permanently deleted.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger d-flex align-items-center"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete location ${
                  locationToDelete?.name || ""
                }`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
      <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Location Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This is the <strong>central dashboard</strong> for viewing and managing all <strong>Delivery/Service Locations</strong> in your system. 
    Each location is tied to a specific branch and has its own defined geographical boundary (polygon) on the map.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">Core Purpose</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">View all locations with their code, name, parent branch, and notes</li>
    <li className="list-group-item">Create new locations with precise polygon-based mapping</li>
    <li className="list-group-item">Edit or update existing location boundaries and details</li>
    <li className="list-group-item">Locations define service zones for deliveries, incentives, workflows, and staff assignments</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Location List – What You See</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Location Code</strong> – Auto-generated unique ID</li>
    <li className="list-group-item"><strong>Location Name</strong> – Custom name (e.g., "Downtown Lahore Hub")</li>
    <li className="list-group-item"><strong>Branch Name</strong> – Parent branch this location belongs to</li>
    <li className="list-group-item"><strong>Notes</strong> – Additional info (optional)</li>
    <li className="list-group-item"><strong>Map Boundary</strong> – Visual polygon shown on hover/view</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Location – Smart Creation Flow</h6>
  <p className="small text-neutral-600 mb-2">Click "Add New Location" to draw a precise service zone:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Branch Name</strong> → Dropdown (pre-filtered active branches)</li>
    <li className="list-group-item"><strong>Location Name</strong> → Give a meaningful name</li>
    <li className="list-group-item">
      <strong>Interactive Map </strong>
      <span className="text-success-600 fw-bold">Most Important Step!</span><br/>
      → Click to draw polygon vertices<br/>
      → Close shape to complete boundary<br/>
      → Defines exact delivery/service area
    </li>
    <li className="list-group-item"><strong>Notes (Optional)</strong> → Add internal remarks</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Actions Available Per Location</h6>
  <ul className="list-group list-group-flush mb-4">
    
    <li className="list-group-item">
      <strong>Edit (Pencil Icon)</strong><br/>
      Update location name, branch, or notes
    </li>
    <li className="list-group-item">
      <strong>Delete (Trash Icon)</strong><br/>
      Remove location (blocked if used in active workflows or incentives)
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Important Notes:</strong><br/>
    • A location without a proper polygon cannot be used in workflows or incentives<br/>
    • Overlapping polygons are allowed but not recommended (can cause routing conflicts)<br/>
    • Always draw tight, accurate boundaries — directly affects rider assignment & incentive accuracy<br/>
    • Locations inherit branch-level settings (taxes, currency, etc.)
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: After creating a branch → Immediately come here → Add accurate locations → Your operations become 10x smarter!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Well-defined locations = Accurate delivery zoning, fair incentives, automated workflows, and zero confusion in field operations.
  </p>
</InfoModal>
    </>
  );
};

export default LocationsListPage;